import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiJson, getToken, setToken as persistToken } from '../api/client.js';

/**
 * @typedef {{
 *   id: number,
 *   name: string,
 *   email: string,
 *   role?: string,
 *   client_id?: number | null,
 *   organization_id?: number | null,
 *   ui_locale?: string,
 *   onboarding_enabled?: boolean,
 *   subscribed?: boolean,
 *   capabilities?: Record<string, boolean>,
 *   can_impersonate?: boolean,
 *   impersonation?: {
 *     active: boolean,
 *     viewing_as?: { id: number, name: string, email: string, role: string },
 *     impersonator?: { id: number, name: string, email: string, role: string },
 *   } | null,
 * }} User
 */

/** @type {import('react').Context<null | {
 *   user: User | null,
 *   booting: boolean,
 *   impersonationLoading: boolean,
 *   login: Function,
 *   register: Function,
 *   logout: Function,
 *   refreshUser: Function,
 *   startImpersonation: (userId: number) => Promise<User>,
 *   stopImpersonation: () => Promise<User>,
 * }>} */
export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(/** @type {User | null} */ (null));
  const [booting, setBooting] = useState(true);
  const [impersonationLoading, setImpersonationLoading] = useState(false);

  const applyAuthResponse = useCallback((data) => {
    persistToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const loadUser = useCallback(async () => {
    const t = getToken();
    if (!t) {
      setUser(null);
      setBooting(false);
      return;
    }
    try {
      const u = await apiJson('/api/user');
      setUser(u);
    } catch {
      persistToken(null);
      setUser(null);
    } finally {
      setBooting(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  useEffect(() => {
    const onExpired = () => {
      persistToken(null);
      setUser(null);
    };
    window.addEventListener('portal:session-expired', onExpired);
    return () => window.removeEventListener('portal:session-expired', onExpired);
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await apiJson('/api/login', {
      method: 'POST',
      body: { email, password },
      skipAuth: true,
    });
    if (data?.two_factor || data?.two_factor_setup) {
      return data;
    }
    return applyAuthResponse(data);
  }, [applyAuthResponse]);

  const register = useCallback(async (payload) => {
    const data = await apiJson('/api/register', {
      method: 'POST',
      body: payload,
      skipAuth: true,
    });
    return applyAuthResponse(data);
  }, [applyAuthResponse]);

  const logout = useCallback(async () => {
    try {
      await apiJson('/api/logout', { method: 'POST' });
    } catch {
      // still clear local session
    }
    persistToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const u = await apiJson('/api/user');
    setUser(u);
    return u;
  }, []);

  const startImpersonation = useCallback(
    async (userId) => {
      setImpersonationLoading(true);
      try {
        const data = await apiJson(`/api/impersonate/${userId}`, { method: 'POST' });
        return applyAuthResponse(data);
      } finally {
        setImpersonationLoading(false);
      }
    },
    [applyAuthResponse],
  );

  const stopImpersonation = useCallback(async () => {
    setImpersonationLoading(true);
    try {
      const data = await apiJson('/api/impersonate/stop', { method: 'POST' });
      return applyAuthResponse(data);
    } finally {
      setImpersonationLoading(false);
    }
  }, [applyAuthResponse]);

  const setSession = useCallback(
    (data) => applyAuthResponse(data),
    [applyAuthResponse],
  );

  const value = useMemo(
    () => ({
      user,
      booting,
      impersonationLoading,
      login,
      register,
      logout,
      refreshUser,
      setSession,
      startImpersonation,
      stopImpersonation,
    }),
    [
      user,
      booting,
      impersonationLoading,
      login,
      register,
      logout,
      refreshUser,
      setSession,
      startImpersonation,
      stopImpersonation,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
